/**
 * In-app bug / feature filing — Platform feedback contract.
 *
 * POST /api/feedback  (REI-API PR #28, docs/feedback.md)
 * Auth: Authorization: Bearer <jwt> — same as other /api/*.
 * Do NOT send reporter identity. Labels (`forge-ui` / `forge-api` +
 * bug / enhancement) are applied server-side from kind + target.
 */

export const FEEDBACK_PATH = '/feedback'

/** UI control value. Mapped to API `kind` via {@link kindFromUiType}. */
export type FeedbackIssueType = 'bug' | 'enhancement'
export type FeedbackKind = 'bug' | 'feature'
export type FeedbackTarget = 'ui' | 'api'
export type FeedbackSeverity = 'low' | 'medium' | 'high' | 'critical'
export type FeedbackPriority = 'low' | 'medium' | 'high'

export interface FeedbackClientContext {
  route: string
  url: string
  userAgent: string
  viewport: { width: number; height: number }
  timestamp: string
  app: 'forge'
}

export interface CreateFeedbackIssueRequest {
  kind: FeedbackKind
  title: string
  body: string
  context?: string
  severity?: FeedbackSeverity
  priority?: FeedbackPriority
  target: FeedbackTarget
}

export interface CreateFeedbackIssueResponse {
  issue: {
    number: number
    htmlUrl: string
    title: string
  }
  repo: string
}

export const FEEDBACK_ERROR = {
  unsigned:
    "You're not signed in or your session expired. Sign in again to file a report.",
  notConfigured:
    "Feedback filing isn't available yet — the API isn't configured.",
  missingToken:
    "Can't file this report — the feedback API isn't configured (missing GitHub token).",
  network: "Couldn't reach the server. Check your connection and try again.",
  generic: "Couldn't file this report. Try again.",
} as const

const TOKEN_HINT = /github[_\s-]?token|feedback[_\s-]?token|not configured|token (is )?(missing|not set|unset)/i

export function kindFromUiType(type: FeedbackIssueType): FeedbackKind {
  return type === 'enhancement' ? 'feature' : 'bug'
}

export function formatFeedbackContext(ctx: FeedbackClientContext): string {
  return [
    `app: ${ctx.app}`,
    `route: ${ctx.route}`,
    `url: ${ctx.url}`,
    `viewport: ${ctx.viewport.width}x${ctx.viewport.height}`,
    `userAgent: ${ctx.userAgent}`,
    `timestamp: ${ctx.timestamp}`,
  ].join('\n')
}

export function collectClientContext(route: string): FeedbackClientContext {
  const href = typeof window !== 'undefined' ? window.location.href : ''
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  const viewport =
    typeof window !== 'undefined'
      ? { width: window.innerWidth, height: window.innerHeight }
      : { width: 0, height: 0 }

  return {
    route,
    url: href,
    userAgent,
    viewport,
    timestamp: new Date().toISOString(),
    app: 'forge',
  }
}

export function buildFeedbackIssueRequest(input: {
  title: string
  body: string
  type: FeedbackIssueType
  target?: FeedbackTarget
  severity?: FeedbackSeverity
  priority?: FeedbackPriority
  context: FeedbackClientContext
  screenshot?: string
}): CreateFeedbackIssueRequest {
  const kind = kindFromUiType(input.type)
  let body = input.body.trim()
  if (input.screenshot) {
    body = `${body}\n\n![screenshot](${input.screenshot})`
  }

  const request: CreateFeedbackIssueRequest = {
    kind,
    title: input.title.trim(),
    body,
    context: formatFeedbackContext(input.context),
    target: input.target ?? 'ui',
  }

  if (kind === 'bug' && input.severity) {
    request.severity = input.severity
  }
  if (kind === 'feature' && input.priority) {
    request.priority = input.priority
  }

  return request
}

export function issueUrlFromResponse(res: CreateFeedbackIssueResponse): string | undefined {
  return res.issue?.htmlUrl
}

export async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file'))
    reader.readAsDataURL(file)
  })
}

export function feedbackErrorMessage(err: unknown, hasClientToken = true): string {
  if (!hasClientToken) return FEEDBACK_ERROR.unsigned

  const status = (err as { status?: number })?.status
  const message = err instanceof Error ? err.message : ''

  if (status === 401) return FEEDBACK_ERROR.unsigned
  if (status === 400 && message && message !== 'Request failed') return message
  if (status === 404 || status === 501) return FEEDBACK_ERROR.notConfigured
  if (status === 503 || TOKEN_HINT.test(message)) return FEEDBACK_ERROR.missingToken
  if (status === 403 && TOKEN_HINT.test(message)) return FEEDBACK_ERROR.missingToken
  if (status === 403) return FEEDBACK_ERROR.unsigned

  if (!status && /failed to fetch|network/i.test(message)) {
    return FEEDBACK_ERROR.network
  }

  if (message && message !== 'Request failed') return message
  return FEEDBACK_ERROR.generic
}
